import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Input,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { CLIENT_FLOW_STORAGE_KEYS, safeParseJson } from "./clientFlowUtils";

const defaultCompany = {
  companyName: "",
  billingEmail: "",
  billingPhone: "",
  primaryDomain: "",
  industry: "",
};

const defaultMember = {
  name: "",
  email: "",
  role: "ADMIN",
};

const ClientCompanyUsersPage = () => {
  const toast = useToast();
  const [company, setCompany] = useState(defaultCompany);
  const [member, setMember] = useState(defaultMember);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedCompany = safeParseJson(window.localStorage.getItem(CLIENT_FLOW_STORAGE_KEYS.COMPANY_PROFILE), defaultCompany);
    const storedMembers = safeParseJson(window.localStorage.getItem(CLIENT_FLOW_STORAGE_KEYS.TEAM_MEMBERS), []);

    setCompany({ ...defaultCompany, ...(storedCompany || {}) });
    setMembers(Array.isArray(storedMembers) ? storedMembers : []);
  }, []);

  const hasCompanyData = useMemo(() => Object.values(company).some((value) => String(value || "").trim()), [company]);

  const saveCompany = () => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CLIENT_FLOW_STORAGE_KEYS.COMPANY_PROFILE, JSON.stringify(company));
    toast({ title: "Company profile saved", status: "success" });
  };

  const addMember = () => {
    if (!member.name.trim() || !member.email.trim()) {
      toast({ title: "Name and email are required", status: "warning" });
      return;
    }

    const nextMembers = [{ ...member, id: Date.now() }, ...members];
    setMembers(nextMembers);
    setMember(defaultMember);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(CLIENT_FLOW_STORAGE_KEYS.TEAM_MEMBERS, JSON.stringify(nextMembers));
    }
  };

  const removeMember = (id) => {
    const nextMembers = members.filter((entry) => entry.id !== id);
    setMembers(nextMembers);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CLIENT_FLOW_STORAGE_KEYS.TEAM_MEMBERS, JSON.stringify(nextMembers));
    }
  };

  return (
    <Stack spacing={8}>
      <Box>
        <Heading size="md" mb={2}>Company Profile</Heading>
        <Text color="gray.600" fontSize="sm">Maintain tenant/company data used for onboarding and billing communications.</Text>
      </Box>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
        <FormControl>
          <FormLabel>Company Name</FormLabel>
          <Input value={company.companyName} onChange={(e) => setCompany((prev) => ({ ...prev, companyName: e.target.value }))} />
        </FormControl>
        <FormControl>
          <FormLabel>Billing Email</FormLabel>
          <Input type="email" value={company.billingEmail} onChange={(e) => setCompany((prev) => ({ ...prev, billingEmail: e.target.value }))} />
        </FormControl>
        <FormControl>
          <FormLabel>Billing Phone</FormLabel>
          <Input value={company.billingPhone} onChange={(e) => setCompany((prev) => ({ ...prev, billingPhone: e.target.value }))} />
        </FormControl>
        <FormControl>
          <FormLabel>Primary Domain</FormLabel>
          <Input value={company.primaryDomain} onChange={(e) => setCompany((prev) => ({ ...prev, primaryDomain: e.target.value }))} />
        </FormControl>
        <FormControl>
          <FormLabel>Industry</FormLabel>
          <Input value={company.industry} onChange={(e) => setCompany((prev) => ({ ...prev, industry: e.target.value }))} />
        </FormControl>
      </Grid>

      <Button colorScheme="blue" alignSelf="start" onClick={saveCompany}>Save Company Profile</Button>

      <Box>
        <Heading size="md" mb={2}>User Access Management</Heading>
        <Text color="gray.600" fontSize="sm" mb={4}>Track users who should have access to this client portal.</Text>

        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 160px auto" }} gap={3} mb={4}>
          <Input placeholder="Full name" value={member.name} onChange={(e) => setMember((prev) => ({ ...prev, name: e.target.value }))} />
          <Input placeholder="user@company.com" value={member.email} onChange={(e) => setMember((prev) => ({ ...prev, email: e.target.value }))} />
          <Select value={member.role} onChange={(e) => setMember((prev) => ({ ...prev, role: e.target.value }))}>
            <option value="ADMIN">Admin</option>
            <option value="BILLING">Billing</option>
            <option value="MEMBER">Member</option>
          </Select>
          <Button colorScheme="blue" onClick={addMember}>Add User</Button>
        </Grid>

        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {members.length > 0 ? (
                members.map((entry) => (
                  <Tr key={entry.id}>
                    <Td>{entry.name}</Td>
                    <Td>{entry.email}</Td>
                    <Td>{entry.role}</Td>
                    <Td>
                      <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeMember(entry.id)}>
                        Remove
                      </Button>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={4}>
                    <Text color="gray.500">No users added yet.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {!hasCompanyData ? (
        <Text color="orange.600" fontSize="sm">Company profile is empty. Fill and save to keep tenant information in this portal flow.</Text>
      ) : null}
    </Stack>
  );
};

export default ClientCompanyUsersPage;
